import * as React from "react";
import { clsx } from "clsx";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { SnapshotGridTableRowMenu } from "./rowMenu";
import { SnapshotGridTableRowVerified } from "./rowVerified";
import { Tooltip } from "@/components/ui/tooltip";
import { SnapshotCheckbox, type SnapshotCheckboxChangeMeta } from "@/components/snapshots/SnapshotCheckbox";
import { RenameSnapshotModal } from "@/modules/snapshots/modals/renameSnapshotModal";
import {
  getSnapshotGridFixedColumnStyle,
  type SnapshotGridColumnWidths,
  type SnapshotGridFixedColumnsVisibility,
} from "./layout";
import type { SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";
import { useDateFormatter } from "@/hooks/useDateFormatter";

/** Marks a family summary row whose folder was deleted: the row stays, frozen at its last value. */
const FolderDeletedBadge: React.FC<{ deletedAt: string }> = ({ deletedAt }) => {
  const { formatDate } = useDateFormatter();
  return (
    <span
      className="shrink-0 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
      title={`Folder deleted on ${formatDate(deletedAt)}; this summary is frozen.`}
      aria-label={`Folder deleted on ${formatDate(deletedAt)}`}
    >
      Folder deleted
    </span>
  );
};

/** Marks a family summary row whose folder lost all its documents: the row stays, frozen at its last value. */
const FolderEmptyBadge: React.FC<{ emptiedAt: string }> = ({ emptiedAt }) => {
  const { formatDate } = useDateFormatter();
  return (
    <span
      className="shrink-0 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
      title={`Folder emptied on ${formatDate(emptiedAt)}; this summary is frozen.`}
      aria-label={`Folder emptied on ${formatDate(emptiedAt)}; this summary is frozen.`}
    >
      Folder empty
    </span>
  );
};

interface Props extends SnapshotGridFixedColumnsVisibility {
  businessReportOutcome: BusinessReportOutcome;
  rowNumber: number;
  isSelected?: boolean;
  onSelectSnapshot?: (checked: boolean, meta: SnapshotCheckboxChangeMeta) => void;
  isHighlighted?: boolean;
  interactionDisabled?: boolean;
  onOpenOutcome?: (outcomeId: string) => void;
  readOnly?: boolean;
  columnWidths?: SnapshotGridColumnWidths;
  expanded?: boolean;
  onToggleSources?: () => void;
  isSourceRow?: boolean;
  disableSnapshotActions?: boolean;
}

export const SnapshotGridTableFixedRow: React.FC<Props> = ({
  businessReportOutcome,
  rowNumber,
  isSelected = false,
  onSelectSnapshot,
  isHighlighted = false,
  interactionDisabled = false,
  onOpenOutcome,
  readOnly = false,
  showSelectionColumn = true,
  showActionsColumn = true,
  columnWidths,
  expanded = false,
  onToggleSources,
  isSourceRow = false,
  disableSnapshotActions = false,
}) => {
  const visibility = { showSelectionColumn, showActionsColumn };
  const [isRenameOpen, setIsRenameOpen] = React.useState(false);

  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onOpenOutcome) {
      onOpenOutcome(businessReportOutcome.id);
      return;
    }
    if (interactionDisabled || readOnly) return;
    setIsRenameOpen(true);
  };

  return (
    <>
      <td
        className={clsx(
          "sticky z-20 py-2 pl-3 pr-1 text-center text-xs tabular-nums text-gray-400 transition-colors group-hover:bg-ivory-mist-400",
          isHighlighted ? "bg-blue-100" : "bg-[#f9f7f5]",
        )}
        style={getSnapshotGridFixedColumnStyle("rowNumber")}
        valign="middle"
      >
        {isSourceRow ? null : rowNumber}
      </td>
      {showSelectionColumn ? (
        <td
          className={clsx(
            "sticky z-20 py-2 pl-4 pr-2 text-sm transition-colors group-hover:bg-ivory-mist-400",
            isHighlighted ? "bg-blue-100" : "bg-[#f9f7f5]",
          )}
          style={getSnapshotGridFixedColumnStyle("selection", visibility)}
          valign="middle"
        >
          {!isSourceRow && onSelectSnapshot && (
            <SnapshotCheckbox
              checked={isSelected}
              onCheckedChange={onSelectSnapshot}
              ariaLabel={`Select snapshot ${businessReportOutcome.name}`}
              disabled={interactionDisabled}
              onClick={e => e.stopPropagation()}
            />
          )}
        </td>
      ) : null}
      {showActionsColumn ? (
        <td
          className={clsx(
            "sticky z-20 px-3 py-2 text-sm font-medium text-gray-900 transition-colors group-hover:bg-ivory-mist-400",
            isHighlighted ? "bg-blue-100" : "bg-[#f9f7f5]",
          )}
          style={getSnapshotGridFixedColumnStyle("actions", visibility)}
          valign="middle"
        >
          {!isSourceRow && (
            <div className="flex-start flex items-center gap-1">
              <SnapshotGridTableRowVerified businessReportOutcome={businessReportOutcome} readOnly={readOnly} />
              <SnapshotGridTableRowMenu
                businessReportOutcome={businessReportOutcome}
                disabled={interactionDisabled || readOnly}
                disableSnapshotActions={disableSnapshotActions}
              />
            </div>
          )}
        </td>
      ) : null}
      <td
        className={clsx(
          "sticky z-20 overflow-hidden border-r border-gray-300 py-2 pl-4 pr-3 text-sm font-medium text-gray-900 transition-colors group-hover:bg-ivory-mist-400 sm:pl-6",
          isHighlighted ? "bg-blue-100" : "bg-[#f9f7f5]",
        )}
        style={getSnapshotGridFixedColumnStyle("name", visibility, columnWidths)}
        valign="middle"
      >
        <div className="flex min-w-0 max-w-full items-center gap-2">
          {onToggleSources ? (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onToggleSources();
              }}
              aria-label={`${expanded ? "Collapse" : "Expand"} source snapshots for ${businessReportOutcome.name}`}
              aria-expanded={expanded}
              className="shrink-0 rounded p-0.5 text-coral hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
            >
              {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            </button>
          ) : isSourceRow ? (
            <span className="pl-5 text-gray-400" aria-hidden="true">
              ↳
            </span>
          ) : null}
          <Tooltip text={businessReportOutcome.name} className="rounded bg-black text-white" delayDuration={50}>
            {onOpenOutcome ? (
              <button
                type="button"
                onClick={handleNameClick}
                className="block min-w-0 max-w-full truncate rounded px-1 text-left hover:bg-ivory-mist-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
                aria-label={`Verify source documents for ${businessReportOutcome.name}`}
              >
                {businessReportOutcome.name}
              </button>
            ) : (
              <div
                onClick={handleNameClick}
                className={clsx(
                  "min-w-0 max-w-full truncate rounded px-1",
                  interactionDisabled || readOnly ? "cursor-default" : "cursor-pointer hover:bg-ivory-mist-600",
                )}
              >
                {businessReportOutcome.name}
              </div>
            )}
          </Tooltip>
          {businessReportOutcome.folder_deleted_at ? (
            <FolderDeletedBadge deletedAt={businessReportOutcome.folder_deleted_at} />
          ) : businessReportOutcome.folder_emptied_at ? (
            <FolderEmptyBadge emptiedAt={businessReportOutcome.folder_emptied_at} />
          ) : null}
        </div>
        {!readOnly && (
          <RenameSnapshotModal
            isOpen={isRenameOpen}
            setIsOpen={setIsRenameOpen}
            businessReportOutcome={businessReportOutcome}
          />
        )}
      </td>
    </>
  );
};

interface PipelineProps {
  row: SnapshotGridRowModel;
  rowNumber: number;
  isHighlighted?: boolean;
  onSelectRow?: (rowId: string) => void;
  ownerName?: string;
}

export const SnapshotGridTablePipelineFixedRow: React.FC<PipelineProps> = ({
  row,
  rowNumber,
  isHighlighted = false,
  onSelectRow,
  ownerName,
}) => {
  const isSummary = row.presentation.kind === "family_summary";
  const role = row.presentation.role;

  return (
    <>
      <td
        className={clsx(
          "sticky left-0 z-20 w-10 min-w-[40px] max-w-[40px] py-2 pl-3 pr-1 text-center text-xs tabular-nums text-gray-500",
          isSummary ? "bg-blue-50" : "bg-white",
          !isSummary && isHighlighted && "bg-blue-100",
        )}
      >
        {rowNumber}
      </td>
      <td
        className={clsx(
          "sticky left-10 z-20 w-[250px] min-w-[250px] max-w-[250px] border-r py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6",
          isSummary ? "border-blue-200 bg-blue-50" : "border-gray-300 bg-white",
          !isSummary && isHighlighted && "bg-blue-100",
        )}
      >
        <button
          type="button"
          className="flex min-w-0 max-w-full items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
          onClick={() => onSelectRow?.(row.id)}
          aria-label={`${isSummary ? "Summary " : ""}${row.name}${role ? ` ${role}` : ""}`}
        >
          {isSummary ? (
            <span
              aria-hidden="true"
              className="shrink-0 rounded border border-blue-200 bg-blue-100 px-1 text-xs font-semibold text-blue-800"
            >
              Σ
            </span>
          ) : null}
          {role ? (
            <span className="shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-700">
              {role}
            </span>
          ) : null}
          <span className="truncate" title={row.name}>
            {row.name}
          </span>
          {row.presentation.folderDeletedAt ? (
            <FolderDeletedBadge deletedAt={row.presentation.folderDeletedAt} />
          ) : row.presentation.folderEmptiedAt ? (
            <FolderEmptyBadge emptiedAt={row.presentation.folderEmptiedAt} />
          ) : null}
        </button>
      </td>
      <td className={clsx("px-4 py-2 text-sm", isSummary ? "bg-blue-50" : "bg-white")}>
        {isSummary ? row.presentation.supplierName || "Not set" : ""}
      </td>
      <td className={clsx("px-4 py-2 text-sm", isSummary ? "bg-blue-50" : "bg-white")}>
        {isSummary ? ownerName || row.presentation.contractFamilyOwnerUserId || "Not set" : ""}
      </td>
      <td className={clsx("px-4 py-2 text-sm", isSummary ? "bg-blue-50" : "bg-white")}>
        {row.presentation.summaryRowKind === "contract_summary"
          ? "Contract summary"
          : row.presentation.summaryRowKind === "document_summary"
            ? "Document summary"
            : "Not available"}
      </td>
    </>
  );
};
